import { formatTimelineRailDateLabels, type TimelineRailDateLabels } from './dateDisplay'

/** 用于跨年/跨日去重的日历键 */
export type TimelineRailCalendarKey = {
  year: string | null
  /** yyyy-mm-dd；无法解析时为 null */
  dateKey: string | null
}

export type TimelineRailMarkerFlags = {
  showYear: boolean
  showMonthDay: boolean
  /** 每轮均展示时刻 */
  showTime: boolean
}

export type TimelineRailRoundMarker = TimelineRailMarkerFlags & {
  labels: TimelineRailDateLabels
  calendar: TimelineRailCalendarKey
}

function calendarKeyFromLabels(labels: TimelineRailDateLabels): TimelineRailCalendarKey {
  const stamp = labels.title.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (stamp) {
    return {
      year: stamp[1],
      dateKey: `${stamp[1]}-${stamp[2]}-${stamp[3]}`,
    }
  }
  if (labels.yearLabel && labels.monthDayLabel) {
    const md = labels.monthDayLabel.match(/^(\d{1,2})月(\d{1,2})日?/) ?? labels.monthDayLabel.match(/^(\d{1,2})[·.](\d{1,2})/)
    if (md) {
      const m = String(Number.parseInt(md[1], 10)).padStart(2, '0')
      const d = String(Number.parseInt(md[2], 10)).padStart(2, '0')
      return { year: labels.yearLabel, dateKey: `${labels.yearLabel}-${m}-${d}` }
    }
  }
  return { year: labels.yearLabel, dateKey: null }
}

/**
 * 时间线自上而下为「新 → 旧」。年份/日期仅在分组边界展示：
 * - 最新一条必展示（若有数据）；
 * - 与上一条（更新）相比跨年/跨日时再展示。
 */
export function computeTimelineRailMarkers(atList: readonly string[]): TimelineRailRoundMarker[] {
  const parsed = atList.map((at) => {
    const labels = formatTimelineRailDateLabels(at)
    return {
      labels,
      calendar: calendarKeyFromLabels(labels),
    }
  })

  return parsed.map((row, index) => {
    const prev = index > 0 ? parsed[index - 1] : null
    const hasYear = Boolean(row.calendar.year)
    const hasDate = Boolean(row.calendar.dateKey ?? row.labels.monthDayLabel)
    const showYear =
      hasYear && (index === 0 || row.calendar.year !== prev?.calendar.year)
    const showMonthDay =
      hasDate &&
      (index === 0 ||
        (row.calendar.dateKey != null &&
          row.calendar.dateKey !== prev?.calendar.dateKey))
    return {
      labels: row.labels,
      calendar: row.calendar,
      showYear,
      showMonthDay,
      showTime: Boolean(row.labels.timeLabel.trim()),
    }
  })
}
